import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { billing } from '@repletoryreach/sdk-api';
import { Stack, Group, Text, Badge, Divider } from '@repletoryreach/sdk-ui';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { formatDateTime, formatCount } from '../../../lib/format';
import { useCredits } from '../../../lib/credits';
import { useRates } from '../../../data/useRates';
import { useT } from '../../../i18n/I18nProvider';
import { sourceKindLabel, unitLabel, maskMsisdn } from './usage-labels';

/**
 * Facts on a row's `meta` that the Details list leaves out. The plan and the
 * pay-as-you-go price are said in a sentence under the working instead of as
 * a raw code and a figure in cents; the last is the platform's own
 * bookkeeping and no part of what the customer was charged.
 */
const UNLISTED_META = new Set(['plan_code', 'list_rate_minor', 'meta_free_allowance']);

/**
 * One ledger entry, explained: what it was, how the amount was worked out
 * (units × rate), what it belonged to, who caused it, and when. The same
 * facts the billing service stored; nothing is inferred here.
 */
export function LedgerEntryExplain({ entry, onClose }: { entry: billing.LedgerEntry | null; onClose: () => void }) {
  const t = useT();
  const credits = useCredits();
  const entryMeta = (entry?.meta ?? null) as Record<string, unknown> | null;
  // A charge made at a plan's price carries the plan's code and the
  // pay-as-you-go price it undercut. The price list has the plan's name; it is
  // read only for such a row, and is usually cached already.
  const planCode = typeof entryMeta?.plan_code === 'string' ? entryMeta.plan_code : null;
  const listRate = typeof entryMeta?.list_rate_minor === 'number' ? entryMeta.list_rate_minor : null;
  const ratesQ = useRates(listRate != null);
  if (!entry) return <Modal opened={false} onClose={onClose} title="" />;
  const e = entry;
  const debit = e.amount_minor < 0;
  const meta = entryMeta && Object.fromEntries(Object.entries(entryMeta).filter(([k, v]) => !UNLISTED_META.has(k) && v != null && v !== ''));
  const units = e.units == null ? null : Number(e.units);
  const planName = ratesQ.data?.meta.plans?.find((p) => p.code === planCode)?.name ?? null;

  return (
    <Modal opened onClose={onClose} title={t('billing.explain.title')} size="md">
      <Stack gap="md">
        <Stack gap={2}>
          <Text role="body" fw={600}>{e.description ?? e.sku_code ?? e.entry_type}</Text>
          <Text role="caption" c="var(--rr-color-text-muted)">{formatDateTime(e.occurred_at)}</Text>
        </Stack>

        <Group justify="space-between" align="baseline">
          <Text role="bodySmall" c="var(--rr-color-text-muted)">{debit ? t('billing.explain.charged') : t('billing.explain.credited')}</Text>
          <Text role="headline" className={`rr-billing__amount rr-billing__amount--${debit ? 'debit' : 'credit'}`}>
            {debit ? '−' : '+'}{credits(Math.abs(e.amount_minor), e.currency)}
          </Text>
        </Group>

        {units != null && e.rate_minor != null && debit && (
          <Row label={t('billing.explain.workedOut')}>
            {t('billing.explain.formula', {
              units: Number.isInteger(units) ? formatCount(units) : units.toLocaleString(undefined, { maximumFractionDigits: 3 }),
              unit: unitLabel(t, e.unit_label, units),
              rate: credits(Number(e.rate_minor), e.currency),
              per: unitLabel(t, e.unit_label, 1)
            })}
            {/* Once the price list has answered, so the plan's name does not
                arrive a moment after the sentence it belongs in. */}
            {listRate != null && listRate > Number(e.rate_minor) && !ratesQ.isPending && (
              <Text role="caption" c="var(--rr-color-text-muted)">
                {planName
                  ? t('billing.explain.planPrice', { plan: planName, list: credits(listRate, e.currency) })
                  : t('billing.explain.planPriceUnnamed', { list: credits(listRate, e.currency) })}
              </Text>
            )}
          </Row>
        )}

        {e.vat_minor > 0 && (
          <Row label={t('billing.explain.vat')}>
            {t('billing.explain.vatLine', { excl: credits(Math.abs(e.amount_minor) - e.vat_minor, e.currency), vat: credits(e.vat_minor, e.currency), pct: e.vat_percent ?? '' })}
          </Row>
        )}

        <Divider />

        <Row label={t('billing.explain.what')}>
          <Group gap={6}>
            <Badge size="sm" variant="light">{sourceKindLabel(t, e.source_kind ?? e.entry_type)}</Badge>
            {e.sku_code && <code>{e.sku_code}</code>}
          </Group>
        </Row>
        {e.contact_msisdn && (
          <Row label={t('billing.explain.contact')}>
            <Link to={`/inbox?q=${encodeURIComponent(e.contact_msisdn)}`}>{maskMsisdn(e.contact_msisdn)}</Link>
          </Row>
        )}
        {e.broadcast_id && (
          <Row label={t('nav.broadcasts')}>
            <Link to={`/broadcasts/${e.broadcast_id}`}>{t('billing.usage.broadcastLink', { id: e.broadcast_id.slice(0, 8) })}</Link>
          </Row>
        )}
        {e.flow_id && (
          <Row label={t('nav.flows')}>
            <Link to={`/flows/${e.flow_id}/builder`}>{t('billing.usage.flowLink', { id: e.flow_id.slice(0, 8) })}</Link>
            {e.flow_run_id && <Text role="caption" c="var(--rr-color-text-muted)"> · {t('billing.explain.run', { id: e.flow_run_id.slice(0, 8) })}</Text>}
          </Row>
        )}
        {e.conversation_id && (
          <Row label={t('nav.inbox')}>
            <Link to={`/inbox?c=${e.conversation_id}`}>{t('billing.explain.openConversation')}</Link>
          </Row>
        )}
        {e.actor && (
          <Row label={t('billing.explain.by')}>
            {e.actor.startsWith('system:') ? t(`billing.actor.${e.actor.slice(7)}`, e.actor) : t('billing.actor.user')}
          </Row>
        )}
        {meta && Object.keys(meta).length > 0 && (
          <>
            <Divider />
            <Stack gap={4}>
              <Text role="caption" c="var(--rr-color-text-muted)">{t('billing.explain.details')}</Text>
              {Object.entries(meta).map(([k, v]) => (
                <Group key={k} justify="space-between" wrap="nowrap" gap="md">
                  <Text role="caption" c="var(--rr-color-text-muted)">{t(`billing.meta.${k}`, k.replace(/_/g, ' '))}</Text>
                  <Text role="caption" truncate>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</Text>
                </Group>
              ))}
            </Stack>
          </>
        )}
        <Text role="caption" c="var(--rr-color-text-muted)">{t('billing.explain.footnote')}</Text>
      </Stack>
    </Modal>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
      <Text role="bodySmall" c="var(--rr-color-text-muted)" style={{ flexShrink: 0 }}>{label}</Text>
      <div style={{ textAlign: 'right', minWidth: 0 }}>{children}</div>
    </Group>
  );
}
